/**
 * Geometrie fürs Platzieren: Grundriss-Rechtecke, Überschneidung, Abstände, Magnet.
 * Reine Funktionen ohne Three.js – gut testbar.
 */
import type { Rect } from "./constants";

export interface PlacedItem {
  id: string;
  k: string; type: string; name: string;
  x: number; y: number; f: number; r: number;
  w: number; d: number; h: number; z: number;
  c: string; lock: boolean;
  /** stellt sich auf Möbel darunter (z. B. Mikrowelle) */
  on: boolean;
  /** flach wie ein Teppich: kollidiert nie */
  flat: boolean;
}

type Geo = Pick<PlacedItem, "x" | "y" | "r" | "w" | "d">;
type P2 = [number, number];

export function corners(it: Geo, x = it.x, y = it.y, r = it.r): P2[] {
  const c = Math.cos(r), s = Math.sin(r), hw = it.w / 2, hd = it.d / 2;
  return ([[-hw, -hd], [hw, -hd], [hw, hd], [-hw, hd]] as P2[]).map(([lx, lz]) => [x + lx * c + lz * s, y + lx * s - lz * c]);
}
export function aabb(it: Geo, x = it.x, y = it.y, r = it.r): Rect {
  const c = Math.abs(Math.cos(r)), s = Math.abs(Math.sin(r));
  const hx = (c * it.w) / 2 + (s * it.d) / 2, hy = (s * it.w) / 2 + (c * it.d) / 2;
  return [x - hx, y - hy, x + hx, y + hy];
}
const rectPts = (r: Rect): P2[] => [[r[0], r[1]], [r[2], r[1]], [r[2], r[3]], [r[0], r[3]]];

/** Trennachsen-Test für zwei konvexe Polygone */
export function polyOverlap(A: P2[], B: P2[], eps = 0.004): boolean {
  for (const P of [A, B]) for (let i = 0; i < P.length; i++) {
    const p = P[i], q = P[(i + 1) % P.length];
    const nx = q[1] - p[1], ny = p[0] - q[0], L = Math.hypot(nx, ny) || 1;
    let a0 = Infinity, a1 = -Infinity, b0 = Infinity, b1 = -Infinity;
    for (const v of A) { const t = (v[0] * nx + v[1] * ny) / L; if (t < a0) a0 = t; if (t > a1) a1 = t; }
    for (const v of B) { const t = (v[0] * nx + v[1] * ny) / L; if (t < b0) b0 = t; if (t > b1) b1 = t; }
    if (a1 <= b0 + eps || b1 <= a0 + eps) return false;
  }
  return true;
}

const vOverlap = (a: PlacedItem, b: PlacedItem) => a.z < b.z + b.h - 0.005 && b.z < a.z + a.h - 0.005;

export class Placement {
  constructor(public colliders: Rect[][], public items: PlacedItem[]) {}

  others(it: PlacedItem) {
    return it.flat ? [] : this.items.filter((o) => o.id !== it.id && o.f === it.f && !o.flat && vOverlap(o, it));
  }

  collides(it: PlacedItem, x = it.x, y = it.y, r = it.r): boolean {
    if (it.flat) return false;
    const P = corners(it, x, y, r);
    for (const c of this.colliders[it.f]) if (polyOverlap(P, rectPts(c))) return true;
    for (const o of this.others(it)) if (polyOverlap(P, corners(o))) return true;
    return false;
  }

  /** nächste Wand / nächstes Möbel in jede Richtung (px = +x, nx = −x, py = +y, ny = −y) */
  gaps(it: PlacedItem, x = it.x, y = it.y, r = it.r) {
    type Gap = { d: number; from: P2; to: P2 } | null;
    const [x0, y0, x1, y1] = aabb(it, x, y, r), cx = (x0 + x1) / 2, cy = (y0 + y1) / 2;
    const g: Record<"px" | "nx" | "py" | "ny", Gap> = { px: null, nx: null, py: null, ny: null };
    const put = (k: keyof typeof g, d: number, from: P2, to: P2) => { if (d > 5) return; const cur = g[k]; if (!cur || d < cur.d) g[k] = { d, from, to }; };
    for (const o of this.colliders[it.f].concat(this.others(it).map((o) => aabb(o)))) {
      if (o[1] < y1 - 0.01 && o[3] > y0 + 0.01) {
        const my = (Math.max(o[1], y0) + Math.min(o[3], y1)) / 2;
        if (o[0] >= cx) put("px", o[0] - x1, [x1, my], [o[0], my]); else if (o[2] <= cx) put("nx", x0 - o[2], [x0, my], [o[2], my]);
      }
      if (o[0] < x1 - 0.01 && o[2] > x0 + 0.01) {
        const mx = (Math.max(o[0], x0) + Math.min(o[2], x1)) / 2;
        if (o[1] >= cy) put("py", o[1] - y1, [mx, y1], [mx, o[1]]); else if (o[3] <= cy) put("ny", y0 - o[3], [mx, y0], [mx, o[3]]);
      }
    }
    return g;
  }

  snapPos(it: PlacedItem, x: number, y: number, grid: number, magnet: boolean): P2 {
    if (grid > 0) { x = Math.round(x / grid) * grid; y = Math.round(y / grid) * grid; }
    if (magnet) {
      const SN = 0.12, g = this.gaps(it, x, y);
      const pick = (a: number | null, b: number | null) => [a, b].filter((v): v is number => v != null && Math.abs(v) < SN).sort((p, q) => Math.abs(p) - Math.abs(q))[0];
      const sx = pick(g.px ? g.px.d : null, g.nx ? -g.nx.d : null); if (sx != null) x += sx;
      const sy = pick(g.py ? g.py.d : null, g.ny ? -g.ny.d : null); if (sy != null) y += sy;
    }
    return [+x.toFixed(3), +y.toFixed(3)];
  }

  stackHeight(it: PlacedItem, x: number, y: number): number {
    let top = 0;
    for (const o of this.items) {
      if (o.id === it.id || o.f !== it.f || o.flat || o.on || o.z >= 0.3) continue;
      if (pointIn(o, x, y)) top = Math.max(top, o.z + o.h);
    }
    return +top.toFixed(3);
  }

  /** sucht spiralförmig einen freien Platz in der Nähe; liefert die Position oder null */
  findFree(it: PlacedItem): P2 | null {
    if (!this.collides(it)) return [it.x, it.y];
    for (let r = 0.1; r <= 3.01; r += 0.1) {
      const n = Math.max(8, Math.round(r * 24));
      for (let i = 0; i < n; i++) {
        const a = (i / n) * Math.PI * 2, x = +(it.x + Math.cos(a) * r).toFixed(2), y = +(it.y + Math.sin(a) * r).toFixed(2);
        if (!this.collides(it, x, y)) return [x, y];
      }
    }
    return null;
  }

  /** an die nächste Wand stellen: Rückseite zur Wand drehen und andocken */
  alignToWall(it: PlacedItem): { x: number; y: number; r: number } | null {
    const opts: [number, "px" | "nx" | "py" | "ny"][] = [[0, "py"], [Math.PI, "ny"], [-Math.PI / 2, "px"], [Math.PI / 2, "nx"]];
    let best: { r: number; k: string; d: number } | null = null;
    for (const [r, k] of opts) { const q = this.gaps(it, it.x, it.y, r)[k]; if (q && q.d > -0.3 && (!best || q.d < best.d)) best = { r, k, d: q.d }; }
    if (!best) return null;
    let { x, y } = it; const s = best.d;
    if (best.k === "px") x += s; if (best.k === "nx") x -= s; if (best.k === "py") y += s; if (best.k === "ny") y -= s;
    return { x: +x.toFixed(3), y: +y.toFixed(3), r: best.r };
  }
}

export function pointIn(o: Geo, px: number, py: number) {
  const c = Math.cos(o.r), s = Math.sin(o.r), dx = px - o.x, dy = py - o.y;
  return Math.abs(dx * c + dy * s) <= o.w / 2 && Math.abs(dx * s - dy * c) <= o.d / 2;
}
